import Alpine from './alpine'
import Sortable from 'sortablejs'

/**
 * Tracks whether a form has diverged from the values it loaded with, and drives
 * the contextual save bar.
 *
 * Comparing serialised snapshots rather than listening for "something changed"
 * means typing a character and undoing it correctly leaves the form clean.
 */
Alpine.data('dirtyForm', (options = {}) => ({
    initial: null,
    dirty: false,
    message: options.message ?? 'Unsaved changes',

    init() {
        this.$nextTick(() => {
            this.initial = this.snapshot()
        })

        this.$el.addEventListener('input', () => this.check())
        this.$el.addEventListener('change', () => this.check())
        this.$el.addEventListener('form-discarded', () => {
            this.$nextTick(() => {
                this.dirty = false
            })
        })

        // A genuine submit is not "leaving with unsaved changes".
        this.$el.addEventListener('submit', () => {
            this.dirty = false
            window.onbeforeunload = null
        })
    },

    snapshot() {
        const data = new FormData(this.$el)
        const entries = []

        for (const [key, value] of data.entries()) {
            if (value instanceof File) continue
            entries.push(`${key}=${value}`)
        }

        return entries.sort().join('&')
    },

    check() {
        if (this.initial === null) return

        const dirty = this.snapshot() !== this.initial

        if (dirty === this.dirty) return

        this.dirty = dirty

        if (dirty) {
            Alpine.store('saveBar').show(this.$el, this.message)
            window.onbeforeunload = () => ''
        } else {
            Alpine.store('saveBar').hide()
            window.onbeforeunload = null
        }
    },
}))

/**
 * Resource list selection: the checkbox column, the header tri-state box and
 * the bulk-action bar that appears once anything is ticked.
 */
Alpine.data('resourceList', (ids = []) => ({
    all: ids,
    selected: [],

    get count() {
        return this.selected.length
    },

    get anySelected() {
        return this.selected.length > 0
    },

    get allSelected() {
        return this.all.length > 0 && this.selected.length === this.all.length
    },

    get indeterminate() {
        return this.anySelected && !this.allSelected
    },

    toggleAll() {
        this.selected = this.allSelected ? [] : [...this.all]
    },

    isSelected(id) {
        return this.selected.includes(id)
    },

    toggle(id) {
        this.selected = this.isSelected(id)
            ? this.selected.filter((value) => value !== id)
            : [...this.selected, id]
    },

    clear() {
        this.selected = []
        this.showingSelectedOnly = false
        this.applyRowFilter()
    },

    /**
     * Narrow the visible rows to the ones that are ticked.
     *
     * Read off the row's own checkbox rather than asking every index view to
     * mark its rows up for this - the value is already there, and a feature
     * that needs a change in twenty templates is a feature that will be wired
     * into three of them.
     */
    showingSelectedOnly: false,

    toggleSelectedOnly() {
        this.showingSelectedOnly = !this.showingSelectedOnly
        this.applyRowFilter()
    },

    applyRowFilter() {
        this.$el.querySelectorAll('tbody tr').forEach((row) => {
            const box = row.querySelector('input[type="checkbox"][value]')

            if (!box) return

            row.hidden = this.showingSelectedOnly && !this.isSelected(box.value)
        })
    },

    init() {
        // Untick something while filtered and it should leave, not linger.
        this.$watch('selected', () => {
            if (this.showingSelectedOnly) this.applyRowFilter()
        })
    },
}))

/**
 * Drag-to-reorder. Writes the resulting order into a hidden input so the
 * surrounding form submits it like any other field - no extra request, and it
 * participates in the dirty check above.
 */
Alpine.data('sortableList', (options = {}) => ({
    init() {
        Sortable.create(this.$el, {
            animation: 150,
            handle: options.handle ?? '[data-sortable-handle]',
            ghostClass: 'opacity-40',
            draggable: options.draggable ?? '[data-sortable-item]',
            onEnd: () => this.sync(),
        })
    },

    sync() {
        const input = options.input ? document.querySelector(options.input) : null
        const ids = [...this.$el.querySelectorAll('[data-sortable-item]')].map(
            (node) => node.dataset.id
        )

        if (input) {
            input.value = ids.join(',')
            input.dispatchEvent(new Event('change', { bubbles: true }))
        }

        this.$el.dispatchEvent(new CustomEvent('reordered', { detail: { ids }, bubbles: true }))
    },
}))

/**
 * Popovers and dropdowns: click-outside and Escape to close, with focus
 * returned to the trigger so keyboard users do not lose their place.
 */
Alpine.data('popover', (options = {}) => ({
    open: false,
    placement: options.placement ?? 'bottom-start',

    toggle() {
        this.open ? this.close() : this.show()
    },

    show() {
        this.open = true
    },

    close(refocus = true) {
        if (!this.open) return
        this.open = false
        if (refocus) this.$refs.trigger?.focus()
    },
}))

/** Modal dialog with focus trapping and scroll locking. */
Alpine.data('modal', (initial = false) => ({
    open: initial,

    show() {
        this.open = true
        document.body.style.overflow = 'hidden'
    },

    close() {
        this.open = false
        document.body.style.overflow = ''
    },
}))

/**
 * Debounced live search that updates the URL, so the browser Back button and a
 * shared link both behave the way an operator expects.
 */
Alpine.data('liveSearch', (options = {}) => ({
    value: options.value ?? '',
    timer: null,

    search() {
        clearTimeout(this.timer)
        this.timer = setTimeout(() => this.$refs.form.requestSubmit(), options.delay ?? 300)
    },

    clear() {
        this.value = ''
        this.$refs.form.requestSubmit()
    },
}))

/** Copy-to-clipboard with a transient confirmation on the button itself. */
Alpine.data('copyable', (text = '') => ({
    copied: false,

    async copy() {
        try {
            await navigator.clipboard.writeText(text)
        } catch {
            // Clipboard API needs a secure context; fall back to a hidden field.
            const field = document.createElement('textarea')
            field.value = text
            field.setAttribute('readonly', '')
            field.style.position = 'fixed'
            field.style.opacity = '0'
            document.body.appendChild(field)
            field.select()
            document.execCommand('copy')
            document.body.removeChild(field)
        }

        this.copied = true
        setTimeout(() => (this.copied = false), 2000)
    },
}))

/** Auto-growing textarea, used for notes and descriptions. */
Alpine.data('autosize', () => ({
    init() {
        this.resize()
        this.$el.addEventListener('input', () => this.resize())
    },

    resize() {
        this.$el.style.height = 'auto'
        this.$el.style.height = `${this.$el.scrollHeight}px`
    },
}))

/**
 * The horizontally scrolling strip of numbers above an index.
 *
 * The arrows only appear when the rail has somewhere to go, and each disables
 * itself at its end. An arrow that is always there and sometimes does nothing
 * teaches people to stop trusting arrows.
 */
Alpine.data('metricStrip', () => ({
    overflowing: false,
    atStart: true,
    atEnd: false,

    init() {
        this.measure()

        // Fonts and the first paint both change the rail's width after init.
        if (typeof ResizeObserver !== 'undefined') {
            new ResizeObserver(() => this.measure()).observe(this.$refs.rail)
        }
    },

    measure() {
        const rail = this.$refs.rail

        if (!rail) return

        const slack = rail.scrollWidth - rail.clientWidth

        this.overflowing = slack > 1
        this.atStart = rail.scrollLeft <= 1
        this.atEnd = rail.scrollLeft >= slack - 1
    },

    page(direction) {
        const rail = this.$refs.rail

        rail.scrollBy({ left: direction * rail.clientWidth * 0.8, behavior: 'smooth' })
    },
}))

/**
 * Which columns stay on screen.
 *
 * The table is drawn by the server with a stylesheet hiding what was already
 * switched off, so a page load never flashes a column that is about to vanish.
 * Toggling here rewrites that stylesheet immediately and saves in the
 * background - a preference that needs confirming is a preference nobody
 * trusts, and one that waits for the network is one that feels broken.
 *
 * Columns are hidden by position. The server knows the order because the
 * headers declared it; this keeps the same list so the two agree.
 */
Alpine.data('columnsMenu', ({ endpoint, view, table, offset = 0, columns = [], hidden = [] }) => ({
    open: false,
    columns,
    hidden: [...hidden],

    get hideable() {
        return this.columns.filter((column) => !column.fixed)
    },

    isHidden(key) {
        return this.hidden.includes(key)
    },

    toggle(key) {
        this.hidden = this.isHidden(key)
            ? this.hidden.filter((item) => item !== key)
            : [...this.hidden, key]

        this.apply()
        this.save()
    },

    showAll() {
        this.hidden = []
        this.apply()
        this.save()
    },

    apply() {
        const positions = this.columns
            .map((column, index) => (!column.fixed && this.isHidden(column.key) ? index + 1 + offset : null))
            .filter((position) => position !== null)

        const id = `${table}-columns`
        let style = document.getElementById(id)

        if (!style) {
            style = document.createElement('style')
            style.id = id
            document.head.appendChild(style)
        }

        style.textContent = positions
            .map((p) => `#${table} > thead > tr > *:nth-child(${p}),#${table} > tbody > tr > *:nth-child(${p}){display:none}`)
            .join('')
    },

    save() {
        clearTimeout(this.timer)

        this.timer = setTimeout(async () => {
            try {
                await fetch(endpoint, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                    },
                    body: JSON.stringify({
                        view,
                        hidden: this.hidden,
                        known: this.hideable.map((column) => column.key),
                    }),
                })
            } catch (error) {
                // The columns are already hidden on this screen. Losing the save
                // costs a re-toggle next time, not the thing they just did.
            }
        }, 300)
    },

    init() {
        // The server wrote a <style> of its own for the initial state. Replace it
        // with one this component owns, so there is a single source of truth.
        this.apply()
    },

    timer: null,
}))

/**
 * A dropdown you can search, which still takes a name that is not on the list.
 *
 * The two halves pull against each other and both matter. Offering only what
 * exists turns "Casa Oliveira", "casa oliveira" and "Casa Olivera" into one
 * supplier instead of three; refusing anything new makes the field useless the
 * first time somebody buys from a company nobody here has heard of. So the
 * list is offered and searchable, and a new name is accepted - but only by
 * choosing it deliberately, never by leaving half-typed text in a box.
 */
Alpine.data('comboBox', (config = {}) => ({
    open: false,
    term: '',
    active: 0,
    value: config.value ?? '',
    items: config.items ?? [],
    allowNew: config.allowNew !== false,

    get matches() {
        const term = this.term.trim().toLowerCase()

        if (term === '') return this.items

        return this.items.filter((item) => item.label.toLowerCase().includes(term))
    },

    /*
     * Only when it is genuinely new. Offering "Use Acme" while Acme is sitting
     * in the list above it invites two rows that are the same supplier.
     */
    get canAddNew() {
        const term = this.term.trim()

        if (! this.allowNew || term === '') return false

        return ! this.items.some((item) => item.label.toLowerCase() === term.toLowerCase())
    },

    toggle() {
        this.open ? this.close() : this.show()
    },

    show() {
        this.open = true
        this.term = ''
        this.active = 0

        // Focus after the panel exists, or it goes nowhere.
        this.$nextTick(() => this.$refs.search?.focus())
    },

    close() {
        this.open = false
    },

    move(by) {
        const last = this.matches.length - (this.canAddNew ? 0 : 1)

        this.active = Math.max(0, Math.min(last, this.active + by))
    },

    choose() {
        if (this.active < this.matches.length) {
            const item = this.matches[this.active]
            if (item) this.pick(item.value)

            return
        }

        if (this.canAddNew) this.pick(this.term.trim())
    },

    pick(value) {
        this.value = value
        this.close()
        this.announce()
    },

    clear() {
        this.value = ''
        this.announce()
    },

    /*
     * Said out loud, because nothing else will. The hidden input is set through
     * Alpine rather than typed into, so no input event reaches the surrounding
     * dirty-check and the save bar would never appear.
     */
    announce() {
        this.$nextTick(() => {
            this.$refs.field?.dispatchEvent(new Event('input', { bubbles: true }))
            this.$refs.field?.dispatchEvent(new Event('change', { bubbles: true }))
        })
    },
}))

/**
 * The customiser's section list: drag, and the buttons, agreeing with each other.
 *
 * The handle was drawn and nothing was bound to it, so the one gesture
 * everybody tries first did nothing at all - and a control that looks live and
 * is dead is worse than no control, because the person assumes the page is
 * broken rather than that the feature is missing.
 *
 * Alpine owns these nodes through x-for, so Sortable cannot simply be left to
 * move them: the two would each believe they had arranged the list and the
 * next render would undo the drag. Sortable's move is put back and the array
 * is changed instead, which is the one thing Alpine will redraw from.
 */
/**
 * A checkbox in a list that saves itself.
 *
 * For a decision somebody makes about a dozen rows in a row, where opening each
 * record to find one field is most of the work. Puts the row back the way it
 * was if the save fails, because a tick that stayed ticked while the server
 * refused it is worse than no tick at all - the next person reads the list and
 * believes it.
 */
/**
 * The reorder sheet's running total.
 *
 * Reads the quantity boxes out of the DOM rather than having each row report
 * itself in. Rows register and unregister as a list is filtered or paged, and
 * a register/forget pair that falls out of step leaves a total counting a row
 * that is no longer on the screen - which is the kind of wrong nobody notices
 * until they are looking at an invoice.
 *
 * The cost sits on each input as a data attribute, so what the total is made of
 * is visible in the markup rather than held in a parallel array.
 */
Alpine.data('reorderSheet', () => ({
    total: 0,
    lines: 0,
    sheet: null,

    init() {
        /*
         * The form, held on to.
         *
         * $el is whichever element the expression is being evaluated against,
         * not the component's root - so recalculate(), called from a quantity
         * box inside a row's own scope, saw the row. It summed one input and
         * reported a total of nought while each row's own line cost was right,
         * which is a convincing way to be wrong.
         */
        this.sheet = this.$el

        this.recalculate()
    },

    recalculate() {
        let total = 0
        let lines = 0

        this.sheet.querySelectorAll('input[data-cost]').forEach((input) => {
            const quantity = parseInt(input.value, 10)

            if (! Number.isFinite(quantity) || quantity <= 0) return

            lines += 1
            total += quantity * (parseFloat(input.dataset.cost) || 0)
        })

        this.total = total
        this.lines = lines
    },

    /*
     * Two decimal places and thousands separators, because this is money being
     * read against a budget rather than a count.
     */
    money(amount) {
        return (Number(amount) || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    },
}))

Alpine.data('rowToggle', ({ endpoint, field = 'internal', checked = false, token = '' }) => ({
    on: checked,
    saving: false,

    async change(next) {
        const was = this.on

        this.on = next
        this.saving = true

        try {
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': token || document.querySelector('meta[name="csrf-token"]')?.content || '',
                },
                body: JSON.stringify({ [field]: next }),
            })

            if (! response.ok) throw new Error('refused')

            const body = await response.json()

            // What came back, not what was clicked. The two agree until
            // something else has changed the row since the page was drawn.
            this.on = body[field] ?? next

            if (body.message) {
                window.dispatchEvent(new CustomEvent('toast', { detail: { message: body.message } }))
            }
        } catch (e) {
            this.on = was

            window.dispatchEvent(new CustomEvent('toast', {
                detail: { message: 'That could not be saved. Nothing has changed.', tone: 'critical' },
            }))
        } finally {
            this.saving = false
        }
    },
}))

Alpine.data('sectionOrder', (initial = []) => ({
    order: initial,

    init() {
        const list = this.$refs.list

        if (! list) return

        Sortable.create(list, {
            animation: 150,
            handle: '[data-sortable-handle]',
            ghostClass: 'opacity-40',
            onEnd: (event) => {
                const from = event.oldIndex
                const to = event.newIndex

                if (from === to || from == null || to == null) return

                /*
                 * Undone before the data changes. Leaving Sortable's move in
                 * place leaves x-for holding a list it did not arrange, and
                 * the row jumps back the next time anything re-renders.
                 */
                event.item.remove()

                const next = [...this.order]
                next.splice(to, 0, next.splice(from, 1)[0])

                this.order = next
                this.announce()
            },
        })
    },

    up(index) {
        if (index === 0) return

        const next = [...this.order]
        ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
        this.order = next
        this.announce()
    },

    down(index) {
        if (index >= this.order.length - 1) return

        const next = [...this.order]
        ;[next[index + 1], next[index]] = [next[index], next[index + 1]]
        this.order = next
        this.announce()
    },

    /*
     * So the Save button can tell whether there is anything to save. Without
     * it, a list dragged into a new order looks identical to one nobody
     * touched.
     */
    announce() {
        this.$nextTick(() => {
            this.$refs.field?.dispatchEvent(new Event('change', { bubbles: true }))
        })
    },
}))
